<template>
  <section
    v-if="visible"
    class="yield-breakdown"
    :class="{ 'yield-breakdown--inline': inline }"
  >
    <header v-if="!inline" class="yield-breakdown__head">
      <span class="yield-breakdown__title text-body-1 text-sans text-bold">
        {{ $t("unit_page.un_yield_breakdown") }}
      </span>
      <span class="yield-breakdown__total text-subtitle text-sans text-bold">
        {{ totalPct }}%
      </span>
    </header>

    <div class="yield-breakdown__bar" role="img" :aria-label="ariaLabel">
      <div
        v-if="rentalPct > 0"
        class="yield-breakdown__seg yield-breakdown__seg--rental"
        :style="{ flexBasis: rentalShare + '%' }"
      />
      <div
        v-if="appreciationPct > 0"
        class="yield-breakdown__seg yield-breakdown__seg--appreciation"
        :style="{ flexBasis: appreciationShare + '%' }"
      />
    </div>

    <div v-if="!inline" class="yield-breakdown__legend">
      <span v-if="rentalPct > 0" class="yield-breakdown__legend-item">
        <span class="yield-breakdown__dot yield-breakdown__dot--rental" aria-hidden="true" />
        <span class="text-body-2 text-sans text-regular">
          {{ $t("unit_page.un_yield_rental") }}
        </span>
        <span class="text-body-2 text-sans text-bold">{{ rentalPct }}%</span>
      </span>
      <span v-if="appreciationPct > 0" class="yield-breakdown__legend-item">
        <span class="yield-breakdown__dot yield-breakdown__dot--appreciation" aria-hidden="true" />
        <span class="text-body-2 text-sans text-regular">
          {{ $t("unit_page.un_yield_appreciation") }}
        </span>
        <span class="text-body-2 text-sans text-bold">{{ appreciationPct }}%</span>
      </span>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { UnitResponseDTO } from "~/services/swagger/Api";

interface IProps {
  propertyInfo: UnitResponseDTO | null | undefined;
  inline?: boolean;
}

const props = withDefaults(defineProps<IProps>(), {
  inline: false,
});
const { t } = useI18n();

const roundOne = (n: number): number => Math.round(n * 10) / 10;

const apr = computed(() => {
  const v = props.propertyInfo?.apr;
  return typeof v === "number" && v > 0 ? v : 0;
});

const appreciationPct = computed(() => {
  const v =
    props.propertyInfo?.finance?.projectTotalIncome?.subfields
      ?.projectionAppreciation;
  return typeof v === "number" && v > 0 ? roundOne(v) : 0;
});

const rentalPct = computed(() => roundOne(apr.value));

const totalPct = computed(() => roundOne(rentalPct.value + appreciationPct.value));

const rentalShare = computed(() => {
  const total = rentalPct.value + appreciationPct.value;
  if (total <= 0) return 0;
  return Math.round((rentalPct.value / total) * 100);
});

const appreciationShare = computed(() => 100 - rentalShare.value);

const visible = computed(() => rentalPct.value > 0 || appreciationPct.value > 0);

const ariaLabel = computed(() =>
  t("unit_page.un_yield_breakdown") +
  `: ${rentalPct.value}% ${t("unit_page.un_yield_rental")}, ` +
  `${appreciationPct.value}% ${t("unit_page.un_yield_appreciation")}`,
);
</script>

<style scoped lang="scss">
.yield-breakdown {
  width: 100%;
  padding: 16px;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: var(--shadow-card);

  &--inline {
    padding: 0;
    background: transparent;
    border: none;
    box-shadow: none;
    border-radius: 0;
    gap: 0;
  }

  &--inline &__bar {
    height: 6px;
  }

  &__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
  }

  &__title {
    color: var(--text-primary);
  }

  &__total {
    color: var(--accent-primary);
  }

  &__bar {
    display: flex;
    width: 100%;
    height: 12px;
    border-radius: var(--radius-pill);
    overflow: hidden;
    background: var(--border-subtle);
  }

  &__seg {
    height: 100%;
    transition: flex-basis 0.4s ease;

    &--rental {
      background: var(--accent-primary);
    }
    &--appreciation {
      background: var(--positive);
    }
  }

  &__legend {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
  }

  &__legend-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: var(--text-secondary);
  }

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    &--rental {
      background: var(--accent-primary);
    }
    &--appreciation {
      background: var(--positive);
    }
  }
}
</style>
